import { Grid, makeStyles } from "@material-ui/core";
import { useFeatureFlagMonitor } from "@nzfm/app-config";
import { useEffect } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { Product } from "../../../api";
import { Client, FLAG_SKIP_NCC_SCREEN, RouteScreen } from "../../../common/types";
import { ReactComponent as LogoImage } from "../../../images/nz-funds-nz-logo.svg";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getClientAsync } from "../../../store/adviser";
import { getNccAsync } from "../../../store/clientApplication";
import ClientDetails from "../../application/ClientDetails";
import DescriptionOfConnections from "../../application/DescriptionOfConnections";
import KiwiSaverOnly from "../../application/KiwiSaverOnly";
import ProductDetails from "../../application/ProductDetails";
import GridLayout, { Actions, Body, Header, Hero } from "../../client/GridLayout";
import ErrorScreen, { ErrorType } from "../../containers/ErrorScreen";
import LoadingScreen from "../../containers/LoadingScreen";
import { Colors, TitleText } from "../../controls/helpers";
import SelectProducts from "./steps/SelectProducts";

export default ({ history, location, match }: RouteComponentProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { client } = useAppSelector((state) => state.adviser);
  const { nccList } = useAppSelector((state) => state.clientApplication);
  const clientId = (match.params as { clientId: string }).clientId;
  const { enabled: skipNccScreenEnabled } = useFeatureFlagMonitor(FLAG_SKIP_NCC_SCREEN);

  useEffect(() => {
    if (clientId) {
      dispatch(getNccAsync());
      dispatch(getClientAsync(clientId));
    }
  }, [clientId]);

  useEffect(() => {
    if (client && !location.pathname.split("/")[4])
      history.replace(
        generateRoute(client.productOptions?.length === 1 ? (client.productOptions.includes(Product.KiwiSaver) ? "kiwisaver" : "client-details") : "select-products")
      );
  }, [client]);

  const nextScreen = (previousScreen: boolean, client: Client) => {
    const direction = previousScreen ? -1 : 1;
    const currentIndex = currentScreen ? screens.indexOf(currentScreen) : -1;
    let i = currentIndex + direction;
    let screen = screens[i];

    while (screen && screen.skipRoute && screen.skipRoute(client)) {
      i += direction;
      screen = screens[i];
    }

    return screen;
  };

  const generateRoute = (route?: string) => `/adviser/sat-application/${clientId}/${!route ? "" : route}`;

  const navigate = (route?: string) => {
    !!route && history.push(generateRoute(route));
  };

  const prevRoute = (client: Client) => {
    navigate(nextScreen(true, client)?.route);
  };

  const nextRoute = (client: Client) => {
    navigate(nextScreen(false, client)?.route);
  };

  if (!client || !nccList?.length) return <LoadingScreen />;

  if (client.emailInvitationSent || client.smsInvitationSent) return <ErrorScreen errorType={ErrorType.InvitationAlreadySent} />;

  const screens: RouteScreen[] = [
    {
      Component: SelectProducts,
      header: "Select products",
      title: "Select products",
      route: "select-products",
      nextRoute,
      skipRoute: (client: Client) => client.productOptions?.length === 1,
    },
    {
      Component: KiwiSaverOnly,
      header: "KiwiSaver",
      title: "KiwiSaver",
      route: "kiwisaver",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => !(client.products?.length === 1 && client.products.includes(Product.KiwiSaver)),
    },
    {
      Component: ClientDetails,
      header: "Client",
      title: "Client",
      route: "client-details",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => client.products?.length === 1 && client.products.includes(Product.KiwiSaver),
    },
    ...(skipNccScreenEnabled
      ? []
      : [
          {
            Component: DescriptionOfConnections,
            header: "Description of connections",
            title: "Description of connections",
            route: "description-of-connections",
            prevRoute,
            nextRoute,
            skipRoute: (client: Client) =>
              (client.products?.length === 1 && client.products.includes(Product.KiwiSaver)) ||
              (!nccList.includes(client.countryOfBirth || "") && !!client.citizenships && !client.citizenships.find((c) => nccList.includes(c))),
          },
        ]),
    {
      Component: ProductDetails,
      header: "Products",
      title: "Products",
      route: "product-details",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => client.products?.length === 1 && client.products.includes(Product.KiwiSaver),
    },
  ];

  const currentScreen = (() => screens.find(({ route }) => route === location.pathname.split("/")[4]))();

  return (
    <Switch>
      {screens.map(({ Component, ...props }) => (
        <Route
          exact
          key={props.route}
          path={match.path + (props.route ? "/" + props.route : "")}
          render={(routeProps) => (
            <GridLayout>
              <Header>
                <Grid container alignItems="center">
                  <LogoImage width={36} height={36} fill={Colors.Black} />
                  &emsp;&emsp;
                  {currentScreen?.title}
                </Grid>
                &nbsp;
              </Header>
              {props.route === "kiwisaver" && (
                <Hero className={classes.hero}>
                  <TitleText>KiwiSaver</TitleText>
                </Hero>
              )}
              <Body>
                <Component {...routeProps} {...props} />
              </Body>
              <Actions />
            </GridLayout>
          )}
        />
      ))}
    </Switch>
  );
};

const useStyles = makeStyles(
  {
    hero: {
      justifyContent: "flex-start !important",
      height: "120px !important",
      backgroundColor: `${Colors.Green} !important`,
      alignItems: "normal !important",
      padding: 24,
      "@media(max-width: 767px)": {
        height: "100px !important",
      },
      "& h4": {
        color: `${Colors.White} !important`,
        lineHeight: 1,
      },
    },
  },
  { name: "NewSatApplication" }
);
